import React, { useCallback, useContext, useLayoutEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGlobe, faHandshake, faMagicWandSparkles, faMagnifyingGlassChart,
  faMoneyBill1Wave, faPeopleGroup, faRocket, faThumbsUp,
} from "@fortawesome/free-solid-svg-icons";
import { Button } from "../../button";
import { useStaticQuery, graphql } from "gatsby";
import { cn, isEmail, mapEdgesToNodes, getStashed } from "../../../lib/helpers";
import PortableText from "../../portableText";
import * as SampleCompany from "../../../../static/data/play-finder.json";
import { freeEmailDomains } from "../../../../static/data/free-email-domains";
import TextEffect from "gpt-type-effect";
import { ImageBuilder } from "../../global/image-builder";
import { SiteContext } from "../../global/site-context";
import { SectionWrapper } from "../wrapper";
import { GetStarted } from "./get-started-banner";
import { Report } from "./play-finder-report";
import { Distraction } from "./play-finder-distract";
import LZString from 'lz-string';
import { SparklesIconOnly } from "../../icon/sparkles-only";
import { makeLogoUrl } from "../../../containers/layout";
import { getSimpleDate } from "../../../lib/client-attribution";
import { TRACKABLE_FIELDS } from "../demo-ux";
import { ATTRIBUTION_PARAM } from "./navigation";

export const TESTING = false;
const API_URL = "/.netlify/functions/play-finder";
const SKIP_FETCH = TESTING;
const SKIP_DELAY = false;
const MAX_LOOKUPS = 3;
const FAKE_DELAY = 5000;
const THINKING_STEP = 1;
const RESULTS_STEP = 2;
export const LLM_MODELS = "Gemini Flash 2.0 + O4 Mini";
const LZ_PREFIX = 'lz-';
export const SHOW_COMPETETORS = true;
export const EMAIL_REVEAL = true;

export const query = graphql`
  query PlayFinderQuery {
    playbooks: allSanityPlaybook(
      sort: { fields: [_createdAt], order: DESC }
      filter: {
        status: { eq: "published" }
      }
    ) {
      edges {
        node {
          ...cardNodePlaybook
        }
      }
    }
    signals: allSanitySignal(
      sort: { fields: [_createdAt], order: DESC }
      filter: {
        status: { eq: "published" }
      }
    ) {
      edges {
        node {
          ...cardNodeSignal
        }
      }
    }
  }
`;

const VALID = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}(\/.*)?$/;
export const validateDomain = (domain) => VALID.test(domain);

const isDomainFree = (domain) => {
  if (!domain) return false;
  return freeEmailDomains.includes(domain.toLowerCase());
};

export const decompress = (data) =>
  !!data.startsWith(LZ_PREFIX)
    ? JSON.parse(LZString.decompressFromBase64(data.substring(LZ_PREFIX.length)))
    : false;

export const compress = (data) =>
  `${LZ_PREFIX}${LZString.compressToBase64(JSON.stringify(data))}`;

export const PlayFinder = ({ section, setBrowserTitle, emailMode }) => {
  const site = useContext(SiteContext);
  const storage = site.metrics.analytics.storage;

  const [messages, setMessages] = useState([]);
  const [domain, setDomain] = useState("");
  const [inputVal, setInputVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(false);
  const [archive, setArchive] = useState([]);
  const [step, setStep] = useState(0);
  const [userKnown, setUserKnown] = useState(!EMAIL_REVEAL || site.metrics.analytics.storage.getItem("user.email"));
  const [autoSubmitEmail, setAutoSubmitEmail] = useState(null);
  const [emailError, setEmailError] = useState("");


  const input = useRef(null);
  const results = useStaticQuery(query);
  const playbooks = mapEdgesToNodes(results.playbooks || {});
  const signals = mapEdgesToNodes(results.signals || {});

  const isRoomie = !!storage.getItem("roomie");

  const doSetStep = useCallback((step) => {
    setStep(step);
    if (window) {
      window.scrollTo(0, 0);
    }
  }, []);

  // Centralized email validation logic
  const validateEmail = useCallback((email, requireEmail = false) => {
    // Clear previous error
    setEmailError("");

    // Email validation
    if (!email) {
      if (requireEmail) {
        setEmailError("Email is required.");
      }
      return false;
    }

    if (!isEmail(email)) {
      setEmailError("Email is invalid.");
      return false;
    }

    // Extract domain from email address (everything after @)
    const freshDomain = email.includes('@') ? email.split('@')[1] : email;

    if (isDomainFree(freshDomain)) {
      setEmailError("Sorry, no personal accounts.");
      return false;
    }

    // If we get here, email is valid - clear any errors
    setEmailError("");
    return true;
  }, []);

  /**
   * Refresh prompt suggestions in AirOps...
   */
  // console.log(JSON.stringify(
  //   playbooks.concat(signals)
  //     .map(n => ({
  //       title: n.title,
  //       summary: n._rawExcerpt[0].children[0].text,
  //       _id: n._id,
  //     }))
  // ));

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const extracted = inputVal.match(/^(?:https?:)?(?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:\/\n]+)/im);
    const freshDomain = extracted?.[1] || inputVal;

    if (inputVal && validateDomain(freshDomain)) {
      if (!site.isAdmin && !isRoomie && (storage.getItem("play-finder-lookups") > MAX_LOOKUPS)) {
        setMessages(messages => messages.concat([
          `You reached the max lookups. Please try again later.`
        ]));
        // Add metric for maxed out lookups. Once per user.
        if (!storage.getItem("play-finder-maxed")) {
          site.metrics.logClick("playgen", "max-lookups");
          storage.setItem("play-finder-maxed", true);
        }
      } else {
        lookupDomain(freshDomain);
        setDomain(freshDomain);
        setInputVal("");
        setLoading(true);
        doSetStep(THINKING_STEP);
        setMessages([]);
        if (!site.metrics.analytics.storage.getItem("user.email")) {
          setUserKnown(false);
        }
      }
    } else {
      setMessages(messages => messages.concat([`Please enter your domain`]));
    }
  }, [inputVal]);

  const emailModeSubmit = useCallback((e) => {
    e.preventDefault();
    const email = inputVal.trim();

    // Validate email with requirement for non-empty
    if (!validateEmail(email, true)) {
      return;
    }

    // Extract domain from email address (everything after @)
    const freshDomain = email.split('@')[1];

    // If we get here, email is valid
    lookupDomain(freshDomain);
    setDomain(freshDomain);
    setInputVal("");
    setLoading(true);
    storage.setItem("user.email", email);
    setUserKnown(true);
    doSetStep(THINKING_STEP);
    setMessages([]);

    if (window._hsq) {
      window._hsq.push(["identify", { email }]);
    }
    const hutk = document.cookie.split(";").find((c) => c.trim().startsWith("hubspotutk="))?.split("=")[1] || "";
    const params = new URLSearchParams(window.location.search);
    const utmFields = TRACKABLE_FIELDS.reduce((acc, id) => {
      acc[`utm_parameter___${id}`] = params.get(`utm_${id}`) || "";
      acc[`utm_parameter___${id}_original`] = storage.getItem(`origin.urlParams.${id}`) || "";
      return acc;
    }, {});
    fetch("/.netlify/functions/hubspot-submit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        formId: "playgent",
        hutk,
        pageUri: window.location.href,
        pageName: document.title,
        fields: {
          email,
          page_cta_path_on_website: params.get(ATTRIBUTION_PARAM) || "",
          utm_parameter___created_date: getSimpleDate(
            `urlParams.${TRACKABLE_FIELDS.find((f) => getStashed({ id: `urlParams.${f}`, storage }))}.created`,
            storage
          ) || "",
          utm_parameter___created_date_original: getSimpleDate(
            `origin.urlParams.${TRACKABLE_FIELDS.find((f) => storage.getItem(`origin.urlParams.${f}`))}.created`,
            storage
          ) || "",
          ...utmFields,
        },
      }),
    }).catch((err) => console.error("HubSpot fallback submit failed:", err));
  }, [inputVal, validateEmail]);

  const handleEmailBlur = useCallback(() => {
    if (!emailMode || !inputVal) {
      setEmailError("");
      return;
    }

    const email = inputVal.trim();

    // Validate email without requiring it to be non-empty (different behavior for blur)
    validateEmail(email, false);
  }, [emailMode, inputVal, validateEmail]);

  // load data from URL param or storage
  useLayoutEffect(() => {
    if (!window) return;
    const params = new URLSearchParams(window.location.search);
    // company results compacted
    const param = params.get("c");
    // domain
    const p = params.get("p") || params.get("d");

    const lzParam = decodeURIComponent(param);
    const data = (param && lzParam.startsWith(LZ_PREFIX))
      ? decompress(lzParam)
      : storage.getItem("play-finder");

    // new lookup data via param
    if (data && !p) {
      console.debug("Found data in storage or param", data);
      doSetCompany(data);
      setDomain(data.domain);
      setLoading(false);
      // lets people in with just a page refresh without this check
      if (!(storage.getItem("play-finder-lookups") > 0)) {
        setUserKnown(true);
      }
      if (param) {
        // remove the param if there is one
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    if (p) {
      setDomain(p);
      setInputVal(p);
    }
  }, []);

  useLayoutEffect(() => {
    // multiple lookups
    const archived = storage.getItem("play-finder-archive");
    if (archived && archived.length > 0) {
      setArchive(archived);
    }

    // roomie status user
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("roomie")) {
      storage.setItem("roomie", true);
    }
  }, [company]);

  const doSetCompany = useCallback((company) => {
    setCompany(company);
    doSetStep(RESULTS_STEP);
    setMessages([]);
    storage.setItem("play-finder", company);
    console.debug("Company set", company);
    setBrowserTitle(`${company.name} plays with Common Room`);

    // stash if accetable
    if (company?.conclusions?.isAppropriate?.value || company?.conclusions?.isTAM?.value) {
      const archived = storage.getItem("play-finder-archive") || [];
      storage.setItem("play-finder-archive", [].concat([company]).concat(
        archived.filter(c => c.domain !== company.domain) || []
      ));
    }

    // If we're in email mode, prepare auto-submit data for the EmailSignup component
    if (emailMode && storage.getItem("user.email")) {
      setAutoSubmitEmail(storage.getItem("user.email"));
      // Temporarily set userKnown to false so EmailPopup renders and can be auto-submitted
      setUserKnown(false);
    }
  }, [emailMode, storage]);

  const reset = useCallback(() => {
    const param = new URLSearchParams(window.location.search).get("c");
    setCompany(null);
    storage.removeItem("play-finder");
    setDomain("");
    setMessages([]);
    setLoading(false);
    setInputVal("");
    setBrowserTitle(site.doc.title);
    if (window && param) {
      // remove the param if there is one
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // input.current.focus();
    doSetStep(0);
  }, []);

  const lookupDomain = useCallback(async (domain) => {
    // Add to the lookup count
    storage.setItem("play-finder-lookups", (storage.getItem("play-finder-lookups") || 0) + 1);
    if (SKIP_FETCH) {
      // faking data fetch
      setLoading(false);
      if (SKIP_DELAY) {
        doSetCompany(SampleCompany);
      } else {
        setTimeout(() => {
          // progressMessages(domain);
          doSetCompany(SampleCompany);
        }, FAKE_DELAY);
      }
    } else {
      // fetching company data
      const sendData = { domain, token: site.buildToken };
      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          inputs: isRoomie ? { ...sendData, roomie: isRoomie } : sendData,
        }),
      });
      const data = await res.json();

      if (!res.error && res.ok) {
        if (data?.result?.name) {
          // plays picked
          setLoading(false);
          doSetCompany(data.result);
        } else {
          setMessages(messages => messages.concat([`Problem finding details for ${domain}.`]));
          console.debug("Data", data);
          setLoading(false);
          doSetStep(0);
        }
      } else {
        setMessages(messages => messages.concat(data?.error || "Error fetching data."));
        setLoading(false);
        doSetStep(0);
      }
    }
  }, []);

  return (
    <div className={emailMode ? "md:pt-12" : "pb-24 mb-40"}>
      {step === 0 ? (
        <div
          className={cn("relative md:pt-12", emailMode ? null : "pb-24")}
          style={emailMode ? null : { minHeight: 600 }}
        >
          <div className="text-center mb-10 max-w-screen-sm mx-auto px-8 md:px-0 relative">
            <span
              className="md:absolute md:top-12 pt-1 md:right-16 text-lemon opacity-30 transition-colors duration-200 text-sm font-semibold tracking-wide uppercase">
              Beta
            </span>
            <PortableText blocks={section._rawSummary} />
          </div>
          <div className="max-w-3/4 md:max-w-screen-sm mx-auto">
            <DomainForm
              inputVal={inputVal}
              setInputVal={setInputVal}
              handleSubmit={emailMode ? emailModeSubmit : handleSubmit}
              loading={loading}
              input={input}
              emailMode={emailMode}
              emailError={emailError}
              handleEmailBlur={handleEmailBlur}
            />
            {messages.length > 0 && (
              <ul className="text-white max-w-screen-sm mx-auto bg-dark-10 rounded-b-xl p-6 pb-3" role="list">
                {messages.map((msg, i) => (
                  <li key={i} className="border-b border-light-5 pt-2 pb-1 mb-2 first:border-t-0">
                    {/* <FontAwesomeIcon icon={faCommenting} className="text-auxiliary" /> */}
                    <TextEffect text={msg} notDisplayCaretAfterFinishes={true} minTypingDelay={300} />
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="text-center text-light-20 text-sm leading-normal my-10 mx-6 sm:mx-2 sm:w-full hover:text-light-50 transition-colors duration-300">
            Powered by <span className="font-medium">{LLM_MODELS}. Results may vary.</span>
          </p>
          {archive.length > 0 && (
            <div className="max-w-screen-sm mx-auto rounded-lg text-light-60 flex justify-center">
              <ul className={cn(
                archive.length < 3 ? "flex flex-col md:flex-row gap-2" : "grid md:grid-cols-2 gap-4",
              )}
              >
                {archive.map((company, i) => (
                  <li key={i}>
                    <a
                      href={`/plays/?c=${encodeURIComponent(compress(company))}`}
                      className="pl-3 hover:text-link flex items-center gap-1"
                    >
                      <div
                        className="bg-white p-1 rounded-md flex justify-center items-center mr-1"
                        style={{ minHeight: 36, minWidth: 36 }}
                      >
                        <ImageBuilder
                          src={makeLogoUrl(company.domain)}
                          alt={company.name}
                          width={28}
                          style={{
                            maxHeight: 28, maxWidth: 28,
                            minHeight: 28, minWidth: 28,
                          }}
                        />
                      </div>
                      {company.domain}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <FloatyIcon icon={faMagnifyingGlassChart} className="text-green-emerald border-green-emerald bg-light-5 opacity-50 hidden absolute lg:flex top-0 right-20" />
          <FloatyIcon icon={faMoneyBill1Wave} className="text-green-emerald border-green-emerald bg-light-5 opacity-50 hidden absolute lg:flex bottom-0 right-20 delay-1000" />
          <FloatyIcon icon={faRocket} className="text-green-emerald border-green-emerald bg-light-5 opacity-50 hidden absolute lg:flex top-0 left-20 delay-1500" />
          <FloatyIcon icon={faHandshake} className="text-green-emerald border-green-emerald bg-light-5 opacity-50 hidden absolute lg:flex bottom-0 left-20 delay-2000" />
          <FloatyIcon icon={faThumbsUp} className="text-green-emerald border-green-emerald bg-light-5 opacity-50 hidden absolute lg:flex top-1/2 right-0 -mt-10 delay-2500" />
          <FloatyIcon icon={faPeopleGroup} className="text-green-emerald border-green-emerald bg-light-5 opacity-50 hidden absolute lg:flex top-1/2 left-0 -mt-10 delay-500" />
        </div>
      ) : step === THINKING_STEP ? (
        <Distraction domain={domain} />
      ) : (step === RESULTS_STEP) ? (
        <>
          <Report
            content={playbooks.concat(signals)}
            company={company}
            reset={reset}
            userKnown={userKnown}
            setUserKnown={setUserKnown}
            autoSubmitEmail={autoSubmitEmail}
            setAutoSubmitEmail={setAutoSubmitEmail}
          />
          <div className="overflow-hidden rounded-2xl text-black">
            <SectionWrapper layout="centered" container="thin" name="getStarted">
              <GetStarted />
            </SectionWrapper>
          </div>
        </>
      ) : null}
    </div>
  );
}

export const FloatyIcon = ({ icon, className }) => (
  <div className={cn(
    "animate-breath border rounded-full h-12 w-12 flex items-center justify-center",
    className
  )}
  >
    <FontAwesomeIcon icon={icon} size="xl" className="max-h-8" />
  </div>
);

const DomainForm = ({ inputVal, setInputVal, handleSubmit, loading, input, emailMode, emailError, handleEmailBlur }) => {
  const site = useContext(SiteContext);

  return (
    <>
      <form onSubmit={handleSubmit} id={emailMode ? "playgent" : null} data-hs-do-not-collect="true">
        <div className={cn(
          "flex gap-2 flex-col sm:flex-row border-4 p-1 rounded-xl bg-dark-30 shadow-lg",
          emailError ? "border-orange-500" : "border-green-emerald"
        )}>
          <input
            type={emailMode ? "email" : "text"}
            name={emailMode ? "email" : "domain"}
            placeholder={loading ? "Processing..." : emailMode ? "Enter your work email" : site.isNotMobile ? "yourdomain.com" : "mydomain.com"}
            className="bg-transparent py-4 sm:py-2.5 px-4 text-white flex-grow outline-none focus:ring-none w-full text-center sm:text-left"
            autoFocus={!!site.isNotMobile}
            ref={input}
            onClick={() => {
              if (input.current) {
                input.current.placeholder = "";
              }
            }}
            onChange={(e) => {
              setInputVal(e.target.value);
            }}
            onBlur={emailMode ? handleEmailBlur : undefined}
            value={inputVal || ""}
            disabled={loading}
          // defaultValue={TESTING ? "testing.io" : null}
          />
          <Button
            text="Show me plays"
            icon={
              <FontAwesomeIcon icon={faMagicWandSparkles} />
            }
            onClick={handleSubmit}
            deactivated={loading}
            className="whitespace-nowrap"
            metrics={{
              action: "play-finder",
              label: "show-me-plays",
            }}
          />
        </div>
      </form>
      {emailError && (
        <div className="text-orange-500 text-sm mt-2 text-center max-w-screen-sm mx-auto">
          {emailError}
        </div>
      )}
    </>
  );
}

export const PlayFinderEmail = ({ section, setBrowserTitle }) =>
  <PlayFinder
    section={section}
    setBrowserTitle={setBrowserTitle}
    emailMode={true}
  />;

const CARDS = [
  {
    title: "Engage economic buyers visiting pricing page",
    description: "Act on the attention of high-intent buyers who are actively researching your solution and ready to make a decision.",
    tag: "Website visits",
    icon: faGlobe
  },
  {
    title: "Convert high-usage free-tier users to $$",
    description: "Identify and target free-tier users who are showing strong engagement patterns and are primed for conversion to paid plans.",
    tag: "Product signals",
    icon: faRocket
  },
  {
    title: "Scale AI messaging with signals and context",
    description: "Leverage user signals, behavioral patterns, and contextual data to deliver highly personalized and relevant AI-powered messaging.",
    tag: "Sales-led signals",
    icon: faMagnifyingGlassChart
  },
  {
    title: "Turn social engagement into pipeline",
    description: "Transform social interactions and engagement into actionable sales opportunities with qualified leads and pipeline growth.",
    tag: "Social signals",
    icon: faHandshake
  }
];

// Incoming cards rise up into place; outgoing cards drift up and away
const CARD_MOTION = {
  active: "translateY(0) scale(1)",
  leaving: "translateY(-12px) scale(0.98)",
  waiting: "translateY(12px) scale(0.98)",
};

export const PlayFinderPreview = ({ section }) => {
  const [{ currentIndex, prevIndex }, setSlide] = useState({ currentIndex: 0, prevIndex: null });
  const [isPaused, setIsPaused] = useState(false);

  useLayoutEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setSlide(({ currentIndex }) => ({
        currentIndex: (currentIndex + 1) % CARDS.length,
        prevIndex: currentIndex,
      }));
    }, 4500);

    return () => clearInterval(interval);
  }, [isPaused]);

  return (
    <div className="max-w-screen-sm mx-auto">
      <PortableText
        blocks={section._rawSummary}
        className="mb-3 mx-8 sm:mx-16 md:mx-0 leading-snug tracking-tight text-center text-xl md:text-3xl text-light-60 font-thin"
      />
      <div
        className="relative opacity-60 hover:opacity-100 transition-opacity duration-500 px-8 py-2"
        style={{ paddingTop: 16, paddingBottom: 20 }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="relative mx-6" style={{ minHeight: 140 }}>
          {CARDS.map((card, index) => {
            const isActive = index === currentIndex;
            const motion = isActive ? "active" : index === prevIndex ? "leaving" : "waiting";
            return (
              <div
                key={index}
                className={cn(
                  "absolute inset-0 transition-all duration-700 ease-out",
                  isActive ? "opacity-100" : "opacity-0 pointer-events-none"
                )}
                style={{ transform: CARD_MOTION[motion] }}
                aria-hidden={!isActive}
              >
                <div className="bg-dark-60 border-2 border-light-20 rounded-2xl p-4 md:p-6 h-full flex flex-col items-center justify-center shadow-lg">
                  <div className="flex items-start justify-center gap-2 mb-4">
                    <SparklesIconOnly className="h-6 w-6 text-white" />
                    <h5 className="md:text-lg font-medium leading-snug text-center md:text-left">{card.title}</h5>
                  </div>
                  <div className="flex gap-3 justify-center items-center">
                    <span className="bg-white md:text-lg text-black font-medium p-1 px-5 md:p-1.5 md:px-5 rounded-full opacity-60">
                      {card.tag}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
